import {
    CSSProperties,
    FC,
    ReactElement,
    useEffect,
} from 'react';

import { useLocation } from 'react-router-dom';

import { Picture } from '../../../components';
import { IconButton, LinkIconButton, Video } from '../../../compositions';
import { MediaBannerInterface } from '../../../entities/@blocks/MediaBanner/MediaBanner';
import { HorizontalAlignment } from '../../../entities/Alignment/Alignment';
import { isSilentLoop } from '../../../entities/Media/Media';
import { scrollQuerySelectorIntoViewIncludingNavigation } from '../../../helpers/scroll';

import './MediaBanner.scss';

interface MediaBannerProps extends MediaBannerInterface {
    className?: string;
}

export const MediaBanner: FC<MediaBannerProps> = ({
    media,
    title,
    titleColor,
    subtitle,
    subtitleColor,
    link,
    linkBackgroundColor,
    anchorTarget,
    className = '',
}): ReactElement => {
    const { hash } = useLocation();

    useEffect((): void => {
        if (hash) {
            scrollQuerySelectorIntoViewIncludingNavigation(hash);
        }
    }, [hash]);

    const cssVariables = {
        '--c-media-banner-title': titleColor,
        '--c-media-banner-subtitle': subtitleColor,
        '--c-media-banner-button-background': linkBackgroundColor,
    } as CSSProperties;

    const scrollToAnchorTarget = (): void => {
        if (anchorTarget) {
            scrollQuerySelectorIntoViewIncludingNavigation(anchorTarget);
        }
    };

    return (
        <div style={cssVariables} className={`media-banner ${className}`}>
            <div className="media-banner__media-wrapper">
                {media.image && !media.video && (
                    <Picture
                        {...media.image}
                        className="media-banner__picture"
                        imageClassName="media-banner__image"
                    />
                )}

                {media.video && (
                    <Video
                        {...media.video}
                        {...isSilentLoop}
                        poster={media.video.poster?.src}
                        className="media-banner__video"
                        videoClassName="media-banner__video-element"
                    />
                )}
            </div>

            <div className="media-banner__cta-wrapper">
                <div className="media-banner__cta-contents-wrapper">
                    {title && (
                        <h2 className="media-banner__title">
                            {title}
                        </h2>
                    )}

                    {subtitle && (
                        <p className="media-banner__subtitle">
                            {subtitle}
                        </p>
                    )}

                    {link && !anchorTarget && (
                        <LinkIconButton
                            hasAnimation
                            icon="arrow-right"
                            iconPos={HorizontalAlignment.right}
                            to={link.href}
                            text={link.label}
                            className="media-banner__button"
                        />
                    )}

                    {link && anchorTarget && (
                        <IconButton
                            icon="chevron-down"
                            iconPos={HorizontalAlignment.right}
                            text={link.label}
                            onClick={scrollToAnchorTarget}
                            className="media-banner__button"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
